import React, { useState, useContext } from 'react';
import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Toolbar,
  Typography,
  Snackbar,
  Alert
} from "@mui/material";
import { ArrowBack, Edit, Delete } from '@mui/icons-material';
import { RoutingContext } from "../../routing";
import { SheetContext } from "../../context";
import './Profile.scss';

function CategoriesScreen() {
  const { navigateToProfile } = useContext(RoutingContext);
  const { categories, addCategory, updateCategory, deleteCategory } = useContext(SheetContext);
  
  // State for dialogs
  const [saving, setSaving] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentCategory, setCurrentCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // State for snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handlers for Add Category
  const handleOpenAddDialog = () => {
    setNewCategoryName('');
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      setSaving(true);
      await addCategory(newCategoryName.trim());
      setSnackbar({
        open: true,
        message: 'Category added successfully',
        severity: 'success'
      });
      handleCloseAddDialog();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: `Error adding category: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handlers for Edit Category
  const handleOpenEditDialog = (category) => {
    setCurrentCategory(category);
    setNewCategoryName(category);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleUpdateCategory = async () => {
    if (!newCategoryName.trim() || newCategoryName.trim() === currentCategory) {
      handleCloseEditDialog();
      return;
    }
    
    try {
      setSaving(true);
      await updateCategory(currentCategory, newCategoryName.trim());
      setSnackbar({
        open: true,
        message: 'Category updated successfully',
        severity: 'success'
      });
      handleCloseEditDialog();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error updating category: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handlers for Delete Category
  const handleOpenDeleteDialog = (category) => {
    setCurrentCategory(category);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteCategory = async () => {
    try {
      setSaving(true);
      await deleteCategory(currentCategory);
      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: `Error deleting category: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handler for Snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <>
      <AppBar color="transparent" position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={navigateToProfile}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Categories
          </Typography>
          <Button></Button>
        </Toolbar>
      </AppBar>

      <div className='screen-content categories-screen'>
        <List>
          <Divider />
          {categories.map((category) => (
            <React.Fragment key={category}>
              <ListItem>
                <ListItemText primary={category} />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    aria-label="edit"
                    onClick={() => handleOpenEditDialog(category)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    edge="end" 
                    aria-label="delete"
                    onClick={() => handleOpenDeleteDialog(category)}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>

        <Button
          color="primary"
          variant="contained"
          onClick={handleOpenAddDialog}
        >
          Add Category
        </Button>
      </div>

      {/* Add Category Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the name of the new category:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddCategory} color="primary" disabled={saving}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update the name of the category:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateCategory} color="primary" disabled={saving}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Category Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{currentCategory}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteCategory} color="error" disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CategoriesScreen;